/**
 * Cart context stamping — LP/PDP settings persisted on line item properties.
 * Merges window.__pageCartContext, window.FDOffer.active, and window.__pdpDiscountOverride.
 */
(function (global) {
  'use strict';

  var PROP = {
    PAGE_PATH: '_page_path',
    SOURCE_SLUG: '_source_slug',
    SOURCE_PATH: '_source_path',
    OTP_CODE: '_otp_discount_code',
    SUB_CODE: '_sub_discount_code',
    AUTO_APPLY_OTP: '_auto_apply_otp_discount_code',
    OTP_PCT: '_otp_discount_percentage',
    SUB_PCT: '_sub_discount_percentage',
    GWP_VARIANT: '_gwp_variant_id',
    DISCOUNT_PROFILE: '_discount_profile',
  };

  function normalizeBool(value) {
    if (typeof value === 'string') {
      var s = value.trim().toLowerCase();
      return s === 'true' || s === '1' || s === 'yes';
    }
    return Boolean(value);
  }

  function isSubscriptionItem(item) {
    var props = (item && item.properties) || {};
    return normalizeBool(props._is_subscription_purchase) ||
      Boolean(item && item.selling_plan) ||
      Boolean(item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
  }

  function isGiftOrAddOnItem(item) {
    var props = (item && item.properties) || {};
    return normalizeBool(props._is_upsell) ||
      normalizeBool(props._welcome_kit_gift) ||
      normalizeBool(props._quantity_welcome_gift) ||
      normalizeBool(props._free_gift) ||
      normalizeBool(props._free_unlock);
  }

  function isPlanUpgradeFlag(value) {
    if (value === undefined || value === null || value === '') return false;
    var normalized = String(value).trim().toLowerCase();
    return normalized !== 'false' && normalized !== '0' && normalized !== 'no';
  }

  function getLineSellingPlanId(item) {
    if (!item) return '';
    var allocation = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    return String(item.selling_plan || (allocation && allocation.id) || '').trim();
  }

  function getLineSellingPlanName(item) {
    var plan = item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    return String((plan && plan.name) || '').toLowerCase();
  }

  function looksLikeAddOnItem(item) {
    var props = (item && item.properties) || {};
    if (normalizeBool(props._add_on)) return true;
    var blob = [
      item && item.handle,
      item && item.product_title,
      item && item.title,
      item && item.url,
      props._parent_pdp_handle
    ].join(' ').toLowerCase();
    return blob.indexOf('add-on') !== -1;
  }

  function isPlanUpgradeGiftLine(item) {
    var props = (item && item.properties) || {};
    return normalizeBool(props._bundle_gift) ||
      normalizeBool(props._box_picker_gift) ||
      normalizeBool(props._welcome_kit_gift) ||
      normalizeBool(props._quantity_welcome_gift) ||
      normalizeBool(props._free_gift) ||
      normalizeBool(props._free_unlock) ||
      String(props._upsell_module || '').toLowerCase() === 'build_box';
  }

  function isPlanUpgradeSeed(item) {
    var props = (item && item.properties) || {};
    return isPlanUpgradeFlag(props._plan_upgrade) && String(props._plan_upgrade_to || '').trim() !== '';
  }

  function parseUpgradePct(raw) {
    var n = parseInt(raw, 10);
    if (Number.isFinite(n) && n > 0 && n < 100) return n;
    return null;
  }

  function isPlanUpgradeForced() {
    var api = global.FDOffer;
    if (api && typeof api.planUpgradeForced === 'function') return api.planUpgradeForced();
    try {
      var raw = sessionStorage.getItem('fd_plan_upgrade');
      var data = raw ? JSON.parse(raw) : null;
      return !!(data && data.enabled);
    } catch (e) {
      return false;
    }
  }

  function getForcedPlanUpgradeSeed() {
    if (!isPlanUpgradeForced()) return null;
    var api = global.FDOffer;
    var cfg = api && typeof api.planUpgradeConfig === 'function' ? api.planUpgradeConfig() : null;
    if (!cfg) {
      try {
        cfg = JSON.parse(sessionStorage.getItem('fd_plan_upgrade') || '');
      } catch (e) {
        cfg = null;
      }
    }
    cfg = cfg && typeof cfg === 'object' ? cfg : {};
    var offer = api && api.active;
    var percent = parseUpgradePct(cfg.percent);
    if (percent == null && offer && Number.isFinite(offer.ninetyDayPct)) {
      percent = parseUpgradePct(offer.ninetyDayPct);
    }
    var savings = parseUpgradePct(cfg.savings);
    if (savings == null && offer && Number.isFinite(offer.ninetyDayPct) && Number.isFinite(offer.subPct)) {
      savings = parseUpgradePct(Math.round(offer.ninetyDayPct - offer.subPct));
    }
    if (savings == null) savings = 20;
    var props = { _plan_upgrade: 'true' };
    if (cfg.toPlanId) props._plan_upgrade_to = String(cfg.toPlanId).trim();
    if (cfg.code) props._plan_upgrade_code = String(cfg.code).trim();
    if (percent != null) props._plan_upgrade_discount_percentage = String(percent);
    if (savings != null) props._plan_upgrade_savings = String(savings);
    return { properties: props };
  }

  function findSeedInItems(items) {
    if (!items || !items.length) return null;
    var i;
    for (i = 0; i < items.length; i++) {
      if (isPlanUpgradeSeed(items[i])) return items[i];
    }
    return null;
  }

  function cartItems() {
    var cart = global.FDCart && typeof global.FDCart.getCart === 'function' ? global.FDCart.getCart() : null;
    return cart && cart.items ? cart.items : null;
  }

  function findPlanUpgradeSeed(items) {
    return findSeedInItems(items) || findSeedInItems(cartItems()) || getForcedPlanUpgradeSeed();
  }

  function findPlanUpgradeSeedFromCart() {
    return findSeedInItems(cartItems()) || getForcedPlanUpgradeSeed();
  }

  function isEligibleForPlanUpgradeCopy(item, seed) {
    if (!item || !seed) return false;
    var props = (item && item.properties) || {};
    if (isPlanUpgradeGiftLine(item) || looksLikeAddOnItem(item)) return false;
    if (isPlanUpgradeFlag(props._plan_upgrade)) {
      var needTo = !String(props._plan_upgrade_to || '').trim();
      var seedTo = String((seed.properties && seed.properties._plan_upgrade_to) || '').trim();
      if (!needTo || !seedTo) return false;
    }
    if (!isSubscriptionItem(item)) return false;
    var planId = getLineSellingPlanId(item);
    var upgradeTo = String((seed.properties && seed.properties._plan_upgrade_to) || '').trim();
    if (upgradeTo && planId && planId === upgradeTo) return false;
    var planName = getLineSellingPlanName(item);
    if (planName && /90|quarter/.test(planName)) return false;
    if (planName && /60|bimonth|bi-month|every 2/.test(planName)) return false;
    return true;
  }

  function copyPlanUpgradeProps(target, seed) {
    if (!target || !seed) return false;
    var src = seed.properties || {};
    if (!isPlanUpgradeFlag(src._plan_upgrade)) return false;
    var dest = target.properties && typeof target.properties === 'object' ? target.properties : {};
    dest._plan_upgrade = 'true';
    var upgradeTo = String(src._plan_upgrade_to || '').trim();
    if (upgradeTo && !String(dest._plan_upgrade_to || '').trim()) dest._plan_upgrade_to = upgradeTo;
    if (src._plan_upgrade_savings && !dest._plan_upgrade_savings) {
      dest._plan_upgrade_savings = String(src._plan_upgrade_savings);
    }
    if (src._plan_upgrade_code && !dest._plan_upgrade_code) {
      dest._plan_upgrade_code = String(src._plan_upgrade_code);
    }
    if (src._plan_upgrade_discount_percentage && !dest._plan_upgrade_discount_percentage) {
      dest._plan_upgrade_discount_percentage = String(src._plan_upgrade_discount_percentage);
    }
    target.properties = dest;
    return true;
  }

  function stampPlanUpgradeOnItems(items) {
    if (!items || !items.length) return;
    var seed = findPlanUpgradeSeed(items);
    if (!seed) return;
    items.forEach(function (item) {
      if (isEligibleForPlanUpgradeCopy(item, seed)) copyPlanUpgradeProps(item, seed);
    });
  }

  function stampPagePath(item) {
    if (!item || typeof item !== 'object') return;
    var props = item.properties && typeof item.properties === 'object' ? item.properties : {};
    if (props[PROP.PAGE_PATH] == null || props[PROP.PAGE_PATH] === '') {
      props[PROP.PAGE_PATH] = global.location.pathname;
    }
    item.properties = props;
  }

  function stampPdpOverride(item) {
    var override = global.__pdpDiscountOverride || null;
    if (!override || !item || typeof item !== 'object') return;

    var props = item.properties && typeof item.properties === 'object' ? item.properties : {};
    var otpCode = override.otpCouponCode || override.couponCode || '';
    var subCode = override.couponCode || '';

    if (otpCode && !props[PROP.OTP_CODE]) props[PROP.OTP_CODE] = String(otpCode);
    if (subCode && !props[PROP.SUB_CODE]) props[PROP.SUB_CODE] = String(subCode);
    if (override.autoApplyOnOtp === true && otpCode && !isSubscriptionItem(item) && !isGiftOrAddOnItem(item) && !props[PROP.AUTO_APPLY_OTP]) {
      props[PROP.AUTO_APPLY_OTP] = 'true';
    }

    if (Number.isFinite(override.discountPercent) && !props[PROP.OTP_PCT]) {
      props[PROP.OTP_PCT] = String(override.discountPercent);
    }

    var subPct =
      Number.isFinite(override.subDiscountPercent) ? override.subDiscountPercent :
      Number.isFinite(override.discountPercent) ? override.discountPercent : null;

    if (subPct !== null && !props[PROP.SUB_PCT]) {
      props[PROP.SUB_PCT] = String(subPct);
    }

    item.properties = props;
  }

  function offerOverridesCatalogPdp() {
    var api = global.FDOffer;
    if (!api || !api.active || !api.active.replacesGlobal) return false;
    if (typeof api.overridesCatalogPdp === 'function') {
      return api.overridesCatalogPdp(global.location && global.location.pathname);
    }
    return true;
  }

  function stampOfferSession(item) {
    var offer = global.FDOffer && global.FDOffer.active;
    if (!offer || !offer.replacesGlobal || !item || typeof item !== 'object') return;

    var props = item.properties && typeof item.properties === 'object' ? item.properties : {};
    var force = offerOverridesCatalogPdp();
    if (offer.otpCode && (force || !props[PROP.OTP_CODE])) props[PROP.OTP_CODE] = String(offer.otpCode);
    if (offer.subCode && (force || !props[PROP.SUB_CODE])) props[PROP.SUB_CODE] = String(offer.subCode);
    if (offer.otpPct != null && Number.isFinite(Number(offer.otpPct)) && (force || !props[PROP.OTP_PCT])) {
      props[PROP.OTP_PCT] = String(offer.otpPct);
    }
    if (offer.subPct != null && Number.isFinite(Number(offer.subPct)) && (force || !props[PROP.SUB_PCT])) {
      props[PROP.SUB_PCT] = String(offer.subPct);
    }
    item.properties = props;
  }

  function stampPageCartContext(item) {
    var ctx = global.__pageCartContext || null;
    if (!ctx || !item || typeof item !== 'object') return;

    var props = item.properties && typeof item.properties === 'object' ? item.properties : {};

    if (ctx.slug && !props[PROP.SOURCE_SLUG]) props[PROP.SOURCE_SLUG] = String(ctx.slug);
    if (ctx.path && !props[PROP.SOURCE_PATH]) props[PROP.SOURCE_PATH] = String(ctx.path);
    if (ctx.path && !props[PROP.PAGE_PATH]) props[PROP.PAGE_PATH] = String(ctx.path);

    var profile = ctx.discountProfile || ctx;
    if (profile) {
      if (profile.otp_code && !props[PROP.OTP_CODE]) props[PROP.OTP_CODE] = String(profile.otp_code);
      if (profile.sub_code && !props[PROP.SUB_CODE]) props[PROP.SUB_CODE] = String(profile.sub_code);
      if (profile.otp_pct != null && !props[PROP.OTP_PCT]) props[PROP.OTP_PCT] = String(profile.otp_pct);
      if (profile.sub_pct != null && !props[PROP.SUB_PCT]) props[PROP.SUB_PCT] = String(profile.sub_pct);
      if (profile.gwp_variant_id && !props[PROP.GWP_VARIANT]) {
        props[PROP.GWP_VARIANT] = String(profile.gwp_variant_id);
      }
    }

    item.properties = props;
  }

  // Ship date from product metafield custom.preorder_text: hidden full text for the email tools, plus a visible copy Shopify prints under the line.
  var PREORDER_PROP = '_ships';
  var PREORDER_LABEL = 'Expected to ship';
  var PREORDER_WAIT_MS = 1000;
  var PREORDER_LOOKUP_LIMIT = 250;
  var preorderTexts = {};
  var preorderLookups = {};

  function variantKey(id) {
    var match = String(id == null ? '' : id).match(/(\d+)$/);
    return match ? match[1] : '';
  }

  // Buy boxes print the metafield with their variant ids (snippets/PBFCM-PDP-cart-summary.liquid).
  // The page wins over the lookup, so the stamp still works when the Storefront API returns null for the variant.
  function readPreorderTextFromPage() {
    var doc = global.document;
    if (!doc || typeof doc.querySelectorAll !== 'function') return;
    doc.querySelectorAll('[data-fd-preorder-text]').forEach(function (el) {
      var text = String(el.getAttribute('data-fd-preorder-text') || '').trim();
      if (!text) return;
      String(el.getAttribute('data-fd-preorder-variants') || '').split(',').forEach(function (id) {
        var key = variantKey(id);
        if (key) preorderTexts[key] = text;
      });
    });
  }

  function loadPreorderText(variantIds) {
    readPreorderTextFromPage();
    var ids = [];
    (variantIds || []).forEach(function (id) {
      var key = variantKey(id);
      if (key && ids.indexOf(key) === -1) ids.push(key);
    });
    var sf = global.Storefront;
    var missing = ids.filter(function (id) {
      return !Object.prototype.hasOwnProperty.call(preorderTexts, id) && !preorderLookups[id];
    }).slice(0, PREORDER_LOOKUP_LIMIT);
    if (missing.length && sf && typeof sf.preorderTextByVariantIds === 'function') {
      var lookup = sf.preorderTextByVariantIds(missing).then(function (texts) {
        Object.keys(texts || {}).forEach(function (id) { preorderTexts[id] = texts[id]; });
      }).catch(function () {
        missing.forEach(function (id) { preorderTexts[id] = ''; });
      }).then(function () {
        missing.forEach(function (id) { delete preorderLookups[id]; });
      });
      missing.forEach(function (id) { preorderLookups[id] = lookup; });
    }
    var pending = [];
    ids.forEach(function (id) {
      if (preorderLookups[id] && pending.indexOf(preorderLookups[id]) === -1) pending.push(preorderLookups[id]);
    });
    return pending.length ? Promise.all(pending) : null;
  }

  // Null when every item's preorder text is known, else a promise that settles once it is (at most PREORDER_WAIT_MS).
  function whenPreorderTextKnown(items) {
    var load = loadPreorderText((items || []).map(function (item) { return item && item.id; }));
    if (!load) return null;
    return Promise.race([load, new Promise(function (resolve) { setTimeout(resolve, PREORDER_WAIT_MS); })]);
  }

  function warmPreorderText(variantIds) {
    loadPreorderText(variantIds);
  }

  // The product page's variants (when root is omitted) and every [data-variant-id] under root.
  function warmPreorderTextIn(root) {
    var ids = [];
    if (!root) {
      var meta = global.ShopifyAnalytics && global.ShopifyAnalytics.meta && global.ShopifyAnalytics.meta.product;
      ((meta && meta.variants) || []).forEach(function (variant) { ids.push(variant && variant.id); });
    }
    (root || global.document).querySelectorAll('[data-variant-id]').forEach(function (el) {
      ids.push(el.getAttribute('data-variant-id'));
    });
    loadPreorderText(ids);
  }

  // "Expected to ship on 11/17" becomes "11/17", so checkout reads "Expected to ship: 11/17".
  function preorderLabelValue(text) {
    var match = /^expected to ship on\s+(.+)$/i.exec(text);
    return match ? match[1] : text;
  }

  // Subscriptions too: Stay AI copies both onto renewal orders, so readers must skip renewals.
  function stampPreorderText(item) {
    if (!item || typeof item !== 'object') return;
    readPreorderTextFromPage();
    var props = item.properties && typeof item.properties === 'object' ? item.properties : null;
    var text = preorderTexts[variantKey(item.id)];
    if (!text || (props && props[PREORDER_PROP])) return;
    props = props || {};
    props[PREORDER_PROP] = text;
    props[PREORDER_LABEL] = preorderLabelValue(text);
    item.properties = props;
  }

  function stampItem(item) {
    stampPagePath(item);
    stampPageCartContext(item);
    stampOfferSession(item);
    stampPdpOverride(item);
    stampPreorderText(item);
  }

  function stampPayload(payload) {
    var items = null;
    if (Array.isArray(payload)) {
      items = payload;
    } else if (payload && typeof payload === 'object' && Array.isArray(payload.items)) {
      items = payload.items;
    }
    if (!items) return;
    items.forEach(stampItem);
    stampPlanUpgradeOnItems(items);
  }

  global.FDCartContext = {
    PROP: PROP,
    stampItem: stampItem,
    stampPayload: stampPayload,
    stampPlanUpgradeOnItems: stampPlanUpgradeOnItems,
    findPlanUpgradeSeed: findPlanUpgradeSeed,
    isEligibleForPlanUpgradeCopy: isEligibleForPlanUpgradeCopy,
    copyPlanUpgradeProps: copyPlanUpgradeProps,
    isPlanUpgradeForced: isPlanUpgradeForced,
    whenPreorderTextKnown: whenPreorderTextKnown,
    warmPreorderText: warmPreorderText,
    warmPreorderTextIn: warmPreorderTextIn,
  };

  if (global.document) {
    global.document.addEventListener('DOMContentLoaded', function () {
      var warm = function () { warmPreorderTextIn(); };
      if (typeof global.requestIdleCallback === 'function') global.requestIdleCallback(warm, { timeout: 2000 });
      else setTimeout(warm, 500);
    });
  }
})(typeof window !== 'undefined' ? window : this);
